import {
    faAlignLeft,
    faBold,
    faCalendarDays,
    faCircleDot,
    faFileArrowUp,
    faFire,
    faFont,
    faGripLines,
    faImages,
    faLayerGroup,
    faListCheck,
    faPalette,
    faRightLeft,
    faScissors,
    faSignature,
    faSliders,
    faTable,
    faTooth,
    faWeight,
} from '@fortawesome/free-solid-svg-icons'
import type { BlockDefinition } from '../types/block-definition'
import LabelBlock from './label-block.svelte'
import TextShortBlock from './text-short-block.svelte'
import TextLongBlock from './text-long-block.svelte'
import TextRichBlock from './text-rich-block.svelte'
import ChoiceOneBlock from './choice-one-block.svelte'
import ChoiceMultipleBlock from './choice-multiple-block.svelte'
import DateBlock from './date-block.svelte'
import SignBlock from './sign-block.svelte'
import ImageAnnotationBlock from './image-annotation-block.svelte'
import BeforeAfterBlock from './before-after-block.svelte'
import FileUploadBlock from './file-upload-block.svelte'
import GlogauScaleBlock from './glogau-scale-block.svelte'
import AcneGradeBlock from './acne-grade-block.svelte'
import RosaceaGradeBlock from './rosacea-grade-block.svelte'
import AcneScarBlock from './acne-scar-block.svelte'
import FitzpatrickScaleBlock from './fitzpatrick-scale-block.svelte'
import CelluliteGradeBlock from './cellulite-grade-block.svelte'
import StretchMarksBlock from './stretch-marks-block.svelte'
import HairTypeBlock from './hair-type-block.svelte'
import HairStrokeBlock from './hair-stroke-block.svelte'
import ImcCalculatorBlock from './imc-calculator-block.svelte'
import DynamicTableBlock from './dynamic-table-block.svelte'
import OdontogramaBlock from './odontograma-block.svelte'
import LabelView from './view/label-view.svelte'
import TextShortView from './view/text-short-view.svelte'
import TextLongView from './view/text-long-view.svelte'
import TextRichView from './view/text-rich-view.svelte'
import ChoiceOneView from './view/choice-one-view.svelte'
import ChoiceMultipleView from './view/choice-multiple-view.svelte'
import DateView from './view/date-view.svelte'
import SignView from './view/sign-view.svelte'
import ImageAnnotationView from './view/image-annotation-view.svelte'
import BeforeAfterView from './view/before-after-view.svelte'
import FileUploadView from './view/file-upload-view.svelte'
import GlogauScaleView from './view/glogau-scale-view.svelte'
import AcneGradeView from './view/acne-grade-view.svelte'
import RosaceaGradeView from './view/rosacea-grade-view.svelte'
import AcneScarView from './view/acne-scar-view.svelte'
import FitzpatrickScaleView from './view/fitzpatrick-scale-view.svelte'
import CelluliteGradeView from './view/cellulite-grade-view.svelte'
import StretchMarksView from './view/stretch-marks-view.svelte'
import HairTypeView from './view/hair-type-view.svelte'
import HairStrokeView from './view/hair-stroke-view.svelte'
import ImcCalculatorView from './view/imc-calculator-view.svelte'
import DynamicTableView from './view/dynamic-table-view.svelte'
import OdontogramaView from './view/odontograma-view.svelte'
import { createDefaultDynamicTableData, createDefaultDynamicTableValue, isDynamicTableValueEmpty } from '../types/dynamic-table'
import { createDefaultOdontogramaValue, isOdontogramaValueEmpty } from '../odontograma/odontograma-data'
import { createDefaultRosaceaGradeValue, isRosaceaGradeValueEmpty } from '../types/rosacea-grade'
import { createDefaultAcneScarValue, isAcneScarValueEmpty } from '../types/acne-scar'
import { createDefaultImcCalculatorValue, isImcCalculatorValueEmpty } from '../types/imc-calculator'

/**
 * Fonte única dos tipos de bloco disponíveis no construtor.
 * Para adicionar um novo bloco: crie o preview (./blocks) e o campo preenchível (./blocks/view)
 * e registre uma nova entrada aqui — paleta, canvas e visualização resolvem tudo a partir deste array.
 */
export const BLOCK_DEFINITIONS: BlockDefinition[] = [
    {
        type: 'label',
        label: 'Texto fixo',
        description: 'Texto informativo, sem campo de resposta',
        category: 'Conteúdo',
        icon: faAlignLeft,
        component: LabelBlock,
        viewComponent: LabelView,
        collectsAnswer: false,
        createDefaultData: () => ({ text: 'Texto de apoio exibido para quem está respondendo o formulário.' }),
        createDefaultValue: () => null,
        isValueEmpty: () => true,
    },
    {
        type: 'text_short',
        label: 'Texto curto',
        description: 'Resposta de uma linha',
        category: 'Campos de texto',
        icon: faFont,
        component: TextShortBlock,
        viewComponent: TextShortView,
        collectsAnswer: true,
        createDefaultData: () => ({}),
        createDefaultValue: () => '',
        isValueEmpty: (value) => !value || value.trim() === '',
    },
    {
        type: 'text_long',
        label: 'Texto longo',
        description: 'Resposta com múltiplas linhas',
        category: 'Campos de texto',
        icon: faGripLines,
        component: TextLongBlock,
        viewComponent: TextLongView,
        collectsAnswer: true,
        createDefaultData: () => ({}),
        createDefaultValue: () => '',
        isValueEmpty: (value) => !value || value.trim() === '',
    },
    {
        type: 'text_rich',
        label: 'Texto formatado',
        description: 'Resposta com formatação (negrito, listas, cores, links...) usando o editor rich text',
        category: 'Campos de texto',
        icon: faBold,
        component: TextRichBlock,
        viewComponent: TextRichView,
        collectsAnswer: true,
        createDefaultData: () => ({}),
        createDefaultValue: () => '',
        isValueEmpty: (value) => !value || value.replace(/<[^>]*>/g, '').trim() === '',
    },
    {
        type: 'choice_one',
        label: 'Escolha única',
        description: 'O respondente escolhe uma opção entre várias',
        category: 'Escolhas',
        icon: faCircleDot,
        component: ChoiceOneBlock,
        viewComponent: ChoiceOneView,
        collectsAnswer: true,
        createDefaultData: () => ({ options: ['Opção 1', 'Opção 2'], allowOther: false, otherLabel: 'Outro' }),
        createDefaultValue: () => null,
        isValueEmpty: (value) => !value,
    },
    {
        type: 'choice_multiple',
        label: 'Escolha múltipla',
        description: 'O respondente pode escolher mais de uma opção',
        category: 'Escolhas',
        icon: faListCheck,
        component: ChoiceMultipleBlock,
        viewComponent: ChoiceMultipleView,
        collectsAnswer: true,
        createDefaultData: () => ({ options: ['Opção 1', 'Opção 2'], allowOther: false, otherLabel: 'Outro' }),
        createDefaultValue: () => [],
        isValueEmpty: (value) => !value || value.length === 0,
    },
    {
        type: 'date',
        label: 'Data',
        description: 'Campo para seleção de data',
        category: 'Outros',
        icon: faCalendarDays,
        component: DateBlock,
        viewComponent: DateView,
        collectsAnswer: true,
        createDefaultData: () => ({}),
        createDefaultValue: () => '',
        isValueEmpty: (value) => !value || value.trim() === '',
    },
    {
        type: 'sign',
        label: 'Assinatura',
        description: 'Área para coleta de assinatura',
        category: 'Outros',
        icon: faSignature,
        component: SignBlock,
        viewComponent: SignView,
        collectsAnswer: true,
        // Escondido da paleta — o fluxo de assinatura de documento (template > Propriedades >
        // "Solicitar assinatura ao preencher") cobre esse caso de uso agora. Mantido funcional (não
        // removido) pra não quebrar modelos que já usam esse bloco, e por poder voltar a ser exposto
        // no futuro.
        hidden: true,
        createDefaultData: () => ({}),
        createDefaultValue: () => null,
        isValueEmpty: (value) => !value,
    },
    {
        type: 'image_annotation',
        label: 'Foto com marcação',
        description: 'Upload de uma ou mais fotos, com lápis, borracha e marcadores personalizáveis (label, cor, unidade e formato) para marcar sobre a imagem',
        category: 'Outros',
        icon: faImages,
        component: ImageAnnotationBlock,
        viewComponent: ImageAnnotationView,
        collectsAnswer: true,
        fixedFullWidth: true,
        createDefaultData: () => ({}),
        createDefaultValue: () => [],
        isValueEmpty: (value) => !value || value.length === 0,
    },
    {
        type: 'before_after',
        label: 'Antes e depois',
        description: 'Até 2 fotos comparadas lado a lado ou com slider sobreposto (escolhido por quem visualiza)',
        category: 'Outros',
        icon: faRightLeft,
        component: BeforeAfterBlock,
        viewComponent: BeforeAfterView,
        collectsAnswer: true,
        fixedFullWidth: true,
        createDefaultData: () => ({}),
        createDefaultValue: () => ({
            before: { src: null, offsetX: 0, offsetY: 0, zoom: 1 },
            after: { src: null, offsetX: 0, offsetY: 0, zoom: 1 },
        }),
        isValueEmpty: (value) => !value?.before?.src && !value?.after?.src,
    },
    {
        type: 'file_upload',
        label: 'Upload de arquivos',
        description: 'Documentos (PDF, Word, Excel...) listados para download, sem prévia visual',
        category: 'Outros',
        icon: faFileArrowUp,
        component: FileUploadBlock,
        viewComponent: FileUploadView,
        collectsAnswer: true,
        fixedFullWidth: true,
        createDefaultData: () => ({}),
        createDefaultValue: () => [],
        isValueEmpty: (value) => !value || value.length === 0,
    },
    {
        type: 'glogau_scale',
        label: 'Escala de Glogau',
        description: 'Classificação do fotoenvelhecimento cutâneo (Tipos I–IV)',
        category: 'Escalas clínicas',
        icon: faLayerGroup,
        component: GlogauScaleBlock,
        viewComponent: GlogauScaleView,
        collectsAnswer: true,
        createDefaultData: () => ({}),
        createDefaultValue: () => null,
        isValueEmpty: (value) => !value,
    },
    {
        type: 'acne_grade',
        label: 'Grau de Acne',
        description: 'Classificação da acne (Graus I–V) com subtipo e nível de inflamação',
        category: 'Escalas clínicas',
        icon: faFire,
        component: AcneGradeBlock,
        viewComponent: AcneGradeView,
        collectsAnswer: true,
        createDefaultData: () => ({}),
        createDefaultValue: () => null,
        isValueEmpty: (value) => !value,
    },
    {
        type: 'rosacea_grade',
        label: 'Subtipos e Grau de Rosácea',
        description: 'Avaliação dos 4 subtipos de rosácea com grau de intensidade',
        category: 'Escalas clínicas',
        icon: faSliders,
        component: RosaceaGradeBlock,
        viewComponent: RosaceaGradeView,
        collectsAnswer: true,
        createDefaultData: () => ({}),
        createDefaultValue: () => createDefaultRosaceaGradeValue(),
        isValueEmpty: (value) => isRosaceaGradeValueEmpty(value),
    },
    {
        type: 'acne_scar',
        label: 'Cicatrizes de Acne',
        description: 'Seleção dos tipos de cicatrizes presentes (Icepick, Boxcar, Rolling, Hipertrófica)',
        category: 'Escalas clínicas',
        icon: faGripLines,
        component: AcneScarBlock,
        viewComponent: AcneScarView,
        collectsAnswer: true,
        createDefaultData: () => ({}),
        createDefaultValue: () => createDefaultAcneScarValue(),
        isValueEmpty: (value) => isAcneScarValueEmpty(value),
    },
    {
        type: 'fitzpatrick_scale',
        label: 'Escala de Fitzpatrick',
        description: 'Classificação do fototipo cutâneo (Tipos I–VI)',
        category: 'Escalas clínicas',
        icon: faPalette,
        component: FitzpatrickScaleBlock,
        viewComponent: FitzpatrickScaleView,
        collectsAnswer: true,
        createDefaultData: () => ({}),
        createDefaultValue: () => null,
        isValueEmpty: (value) => !value,
    },
    {
        type: 'cellulite_grade',
        label: 'Grau de Celulite',
        description: 'Classificação do grau de celulite (Graus I–IV)',
        category: 'Escalas clínicas',
        icon: faTable,
        component: CelluliteGradeBlock,
        viewComponent: CelluliteGradeView,
        collectsAnswer: true,
        createDefaultData: () => ({}),
        createDefaultValue: () => null,
        isValueEmpty: (value) => !value,
    },
    {
        type: 'stretch_marks',
        label: 'Estrias',
        description: 'Classificação do tipo de estrias presentes (Rubra ou Alba)',
        category: 'Escalas clínicas',
        icon: faRightLeft,
        component: StretchMarksBlock,
        viewComponent: StretchMarksView,
        collectsAnswer: true,
        createDefaultData: () => ({}),
        createDefaultValue: () => null,
        isValueEmpty: (value) => !value,
    },
    {
        type: 'hair_type',
        label: 'Tipo de Cabelo',
        description: 'Classificação do tipo capilar (Liso, Ondulado ou Crespo)',
        category: 'Escalas clínicas',
        icon: faScissors,
        component: HairTypeBlock,
        viewComponent: HairTypeView,
        collectsAnswer: true,
        createDefaultData: () => ({}),
        createDefaultValue: () => null,
        isValueEmpty: (value) => !value,
    },
    {
        type: 'hair_stroke',
        label: 'Espessura do Fio',
        description: 'Classificação da espessura do fio capilar (Fino, Médio ou Grosso)',
        category: 'Escalas clínicas',
        icon: faGripLines,
        component: HairStrokeBlock,
        viewComponent: HairStrokeView,
        collectsAnswer: true,
        createDefaultData: () => ({}),
        createDefaultValue: () => null,
        isValueEmpty: (value) => !value,
    },
    {
        type: 'imc_calculator',
        label: 'Cálculo de IMC',
        description: 'Campos de peso e altura com cálculo automático do IMC e classificação',
        category: 'Calculadoras',
        icon: faWeight,
        component: ImcCalculatorBlock,
        viewComponent: ImcCalculatorView,
        collectsAnswer: true,
        createDefaultData: () => ({}),
        createDefaultValue: () => createDefaultImcCalculatorValue(),
        isValueEmpty: (value) => isImcCalculatorValueEmpty(value),
    },
    {
        type: 'dynamic_table',
        label: 'Tabela Dinâmica',
        description: 'Tabela com colunas configuráveis, adição de linhas e assinatura por linha',
        category: 'Especiais',
        icon: faTable,
        component: DynamicTableBlock,
        viewComponent: DynamicTableView,
        collectsAnswer: true,
        fixedFullWidth: true,
        createDefaultData: () => createDefaultDynamicTableData(),
        createDefaultValue: () => createDefaultDynamicTableValue(),
        isValueEmpty: (value) => isDynamicTableValueEmpty(value),
    },
    {
        type: 'odontograma',
        label: 'Odontograma',
        description: 'Mapa dental interativo para marcação de tratamentos por dente e face',
        category: 'Especiais',
        icon: faTooth,
        component: OdontogramaBlock,
        viewComponent: OdontogramaView,
        collectsAnswer: true,
        fixedFullWidth: true,
        createDefaultData: () => ({}),
        createDefaultValue: () => createDefaultOdontogramaValue(),
        isValueEmpty: (value) => isOdontogramaValueEmpty(value),
    },
]

export function getBlockDefinition(type: string): BlockDefinition | undefined {
    return BLOCK_DEFINITIONS.find((definition) => definition.type === type)
}

/** Largura efetiva (1-3) para o grid: tipos `fixedFullWidth` ignoram `block.width` e sempre ocupam a linha inteira. */
export function getEffectiveBlockWidth(block: { type: string; width?: number }): 1 | 2 | 3 {
    if (getBlockDefinition(block.type)?.fixedFullWidth) return 3
    return (block.width as 1 | 2 | 3) ?? 3
}

export function getBlockCategories(): string[] {
    return [...new Set(BLOCK_DEFINITIONS.filter((definition) => !definition.hidden).map((definition) => definition.category))]
}
